import { ELoadingType, EMGirlType, Loading, MgirlNotice } from '@bilibili-maoer/prelude'
import { api, format } from '@bilibili-maoer/web-utils'

import { useCallback, useEffect, useState } from 'react'

import { Box, Stack, Tab, Tabs, css, styled } from '@mui/material'
import moment from 'moment'
import useMount from 'react-use/esm/useMount'
import { match } from 'ts-pattern'

import { Button } from '~/core/components/button'
import { StyledPaper } from '~/core/components/layout'
import { asMissEvanPagination } from '~/core/pagination'
import { initDateTimeRange } from '~/core/utils/date'
import { DateRange } from '~/features/guild/components/date-range'
import { FormattedTable } from '~/features/guild/components/table'
import { TableColumn } from '~/features/guild/components/table/types'
import { DataBlocksTitle, GuildTitle } from '~/features/guild/components/title'
import { DateRangeWrapper } from '~/features/guild/components/wrappers'
import { DAYS_OF_A_WEEK, GUILD_DEFAULT_INCOME_TYPE, TAB_LIST } from '~/features/guild/constants'
import type {
  GuildLiveProfitListParams,
  GuildProfitDetailItem,
  GuildProfitErrorType,
} from '~/features/guild/profit/types'
import type { ListTableRow } from '~/features/guild/types'
import { EGuildIncomeType } from '~/features/guild/types'
import { GuildApi } from '~/services/fm/guild'
import useAppSearchParam from '~/shared/navigation/use-app-search-params'
import type { Days } from '~/shared/types/base/units'

const StyledPaperContainer = styled(StyledPaper)(
  ({ theme }) => css`
    background-color: ${theme.palette.background.default};
  `
)
const StyledHeader = styled('div')`
  padding-bottom: 20px;
`
const StyledFilter = styled('div')`
  padding-bottom: 15px;
`
const StyledContainer = styled('div')`
  height: 100%;
  padding-bottom: 50px;
`
const FormattedMgirlNotFound = styled(MgirlNotice)`
  padding-top: 100px;
  text-align: center;
  img {
    height: 170px;
  }
`
const StyledLink = styled('a')(
  ({ theme }) => css`
    color: ${theme.palette.text.primary};
    opacity: 1;
    text-decoration: none;
    &:hover {
      opacity: 0.7;
    }
  `
)
const StyledSecondaryCell = styled('span')(
  ({ theme }) => css`
    color: ${theme.palette.text.secondary};
  `
)

const columns: TableColumn<ListTableRow<GuildProfitDetailItem>>[] = [
  {
    field: 'confirm_time',
    headerTitle: '时间',
    renderBodyCell: (item) => (
      <StyledSecondaryCell>
        {format.formatUTCTimeBySeconds(item.row.confirm_time, 'YYYY-MM-DD HH:mm:ss')}
      </StyledSecondaryCell>
    ),
  },
  {
    field: 'username',
    headerTitle: '发起方',
    renderBodyCell: (item) => (
      <StyledLink href={`${api.MISSEVAN_SITE_MAIN}/${item.row.user_id}`} target="_blank">
        {item.row.username}
      </StyledLink>
    ),
  },
  {
    field: 'title',
    headerTitle: '收益来源',
    renderBodyCell: (item) => item.row.title,
  },
  {
    field: 'revenue',
    headerTitle: '金额（元）',
    renderBodyCell: (item) => (item.row.revenue > 0 ? `+${item.row.revenue}` : item.row.revenue),
  },
  {
    field: 'status',
    headerTitle: '状态',
    renderBodyCell: (item) => <StyledSecondaryCell>{item.row.status ? '交易成功' : '交易失败'}</StyledSecondaryCell>,
  },
]

export const GuildProfit = () => {
  const [searchParams, setSearchParams] = useAppSearchParam()
  const time = initDateTimeRange({
    startTime: undefined,
    endTime: undefined,
    // 设置默认时间范围为 7 天
    daysRange: DAYS_OF_A_WEEK as Days,
  })
  const [timeRange, setTimeRange] = useState(time)
  const currentType = searchParams.get('type') || GUILD_DEFAULT_INCOME_TYPE.toString()
  const parseType = parseInt(currentType)
  // 判断当前查询 type 是否有效，如果无效则取默认类型
  const type = parseType in EGuildIncomeType ? parseType : GUILD_DEFAULT_INCOME_TYPE
  // 请求接口参数
  const [queryParams, setQueryParams] = useState<GuildLiveProfitListParams>({
    start_date: moment(timeRange.startTime).format('YYYY-MM-DD'),
    end_date: moment(timeRange.endTime).format('YYYY-MM-DD'),
    p: parseInt(searchParams.get('page') || '1'),
    type,
  })
  const [totalIncome, setTotalIncome] = useState<number>(0)

  const {
    data: guildIncomeData,
    isFetching,
    isError,
    isLoading,
    error,
  } = GuildApi.getGuildLiveProfitList.useQuery(queryParams)
  const [getGuildLiveTotalProfit, guildLiveTotalProfit] = GuildApi.getGuildLiveTotalProfit.useLazyQuery()
  const { data: incomeList = [], total, pagination } = guildIncomeData || {}
  const incomePagination = pagination && asMissEvanPagination(pagination)

  const handleSetSearchParams = useCallback(
    (params: Partial<GuildLiveProfitListParams>) => {
      // 设置 url 上的参数 type, page
      setSearchParams(
        new URLSearchParams({
          // TODO: 后续把这个 fallback 的处理抽出去，作为一个 hook
          type: (params?.type ?? queryParams.type ?? GUILD_DEFAULT_INCOME_TYPE)?.toString(),
          page: (params?.p ?? queryParams.p)?.toString() || '1',
        })
      )
    },
    [queryParams, setSearchParams]
  )
  const handleSearchParamsChange = useCallback(
    (params: Partial<GuildLiveProfitListParams>) => {
      handleSetSearchParams(params)
      // 改变当前接口请求参数
      setQueryParams({ ...queryParams, ...params })
    },
    [queryParams, handleSetSearchParams]
  )
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // 改变当前接口请求参数
    setQueryParams({
      ...queryParams,
      start_date: moment(timeRange.startTime).format('YYYY-MM-DD'),
      end_date: moment(timeRange.endTime).format('YYYY-MM-DD'),
    })
  }

  // 如果查询的 page 超过了当前数据最大页数，则 page 设置为 1 并且重新请求接口
  useEffect(() => {
    if ((incomePagination?.page || 1) > (incomePagination?.page_count || 1)) {
      handleSearchParamsChange({ p: 1 })
    }
  }, [incomePagination, handleSearchParamsChange, getGuildLiveTotalProfit])
  useEffect(() => {
    if (guildLiveTotalProfit.data?.total || total) {
      setTotalIncome(guildLiveTotalProfit.data?.total || total || 0)
    }
  }, [guildLiveTotalProfit, total])

  useMount(() => {
    handleSetSearchParams({ type: queryParams.type, p: queryParams.p })
    if (queryParams.p !== 1) {
      getGuildLiveTotalProfit(queryParams)
    }
  })

  return (
    <StyledPaperContainer elevation={0}>
      {/* 增加首屏 Loading，避免 error 后，tab 栏闪烁 */}
      {match(isLoading)
        .with(true, () => <Loading type={ELoadingType.Cat} />)
        .otherwise(() =>
          match(isError)
            .with(true, () => (
              <FormattedMgirlNotFound
                image={EMGirlType.NotFound}
                // 解决 error 下可能不存在 data 的 eslint 报错
                description={(error as unknown as GuildProfitErrorType)?.data?.info ?? '未知错误'}
              />
            ))
            .otherwise(() => (
              <>
                <StyledHeader>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '15px' }}>
                    <Tabs
                      value={queryParams.type}
                      onChange={(event, value) => handleSearchParamsChange({ type: value, p: 1 })}
                      textColor="secondary"
                      indicatorColor="secondary"
                    >
                      {TAB_LIST.map((item) => (
                        <Tab key={item.type} value={item.type} label={item.name} disableRipple />
                      ))}
                    </Tabs>
                  </Box>
                  <span>{TAB_LIST[queryParams.type]?.guildDesc}</span>
                </StyledHeader>
                <StyledFilter>
                  <DateRangeWrapper>
                    <DateRange time={timeRange} onTimeRangeChange={setTimeRange} />
                    <Button variant="contained" onClick={handleFormSubmit}>
                      确定
                    </Button>
                  </DateRangeWrapper>
                </StyledFilter>
                {match(isFetching)
                  .with(true, () => <Loading type={ELoadingType.Cat} />)
                  .otherwise(() => (
                    <StyledContainer>
                      {incomeList?.length ? (
                        <Stack spacing={2}>
                          <DataBlocksTitle>
                            <GuildTitle
                              title={`${queryParams.start_date} 至 ${queryParams.end_date} 全部${
                                TAB_LIST[queryParams.type]?.name
                              } ${totalIncome} 元`}
                            />
                          </DataBlocksTitle>
                          <FormattedTable
                            columns={columns}
                            rows={incomeList}
                            page={queryParams.p}
                            count={incomePagination?.page_count || 0}
                            onChange={(e, page) => handleSearchParamsChange({ p: page })}
                          />
                        </Stack>
                      ) : (
                        <FormattedMgirlNotFound image={EMGirlType.NotFound} description="没有查询到数据哦~" />
                      )}
                    </StyledContainer>
                  ))}
              </>
            ))
        )}
    </StyledPaperContainer>
  )
}
